import { IExpression } from "@@expressions";
import Timeline from "@mui/lab/Timeline";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import React from "react";
import { createScopedLogger } from "../../../common";
import IconComponent from "../../../common/components/IconComponent";
import LoadingOverlay from "../../../common/components/LoadingOverlay";
import { IExpressionEvaluatorContext } from "../../../ExpressionsModule/Models/IExpressionEvaluatorContext";
import { VariableNames } from "../../Constants/VariableNames";
import { useChildContexts } from "../../Hooks/useChildContexts";
import { useComponentSelection } from "../../Hooks/useComponentSelection";
import useObservedExpression from "../../Hooks/useObservedExpression";
import { TimelineModel, TimelinePosition } from "../../Models/TimelineModel";
import ComponentFactory from "../ComponentFactory";
import IComponentProps from "../IComponentProps";

const logger = createScopedLogger("TimelineComponent");

const positionMap: Record<TimelinePosition, "alternate" | "left" | "right"> = {
  [TimelinePosition.Alternate]: "alternate",
  [TimelinePosition.Left]: "left",
  [TimelinePosition.Right]: "right",
};

// -----------------------------------------------------------------------
// Per-item sub-component — lets us call hooks per item without violating
// the rules of hooks (no hooks inside loops).
// -----------------------------------------------------------------------
type TimelineItemRendererProps = {
  component: TimelineModel;
  itemContext: IExpressionEvaluatorContext;
  itemIndex: number;
  componentPath: string[];
  itemKey: string | number;
};

const TimelineItemRenderer: React.FC<TimelineItemRendererProps> = ({ component, itemContext, itemIndex, componentPath, itemKey }) => {
  const [iconNameFromExpr] = useObservedExpression<string>(component.GetIconName as IExpression | undefined, itemContext, "TimelineItemRenderer");
  const resolvedIconName = iconNameFromExpr || component.IconName;

  const contentChild = component.Children[0];
  const oppositeChild = component.Children[1];

  return (
    <TimelineItem>
      {oppositeChild && (
        <TimelineOppositeContent sx={{ m: "auto 0" }}>
          <ComponentFactory
            parent={component}
            key={`${itemContext.StableId}-opposite-${oppositeChild.Id}`}
            component={oppositeChild}
            context={itemContext}
            componentPath={[...componentPath, oppositeChild.Id]}
          />
        </TimelineOppositeContent>
      )}
      <TimelineSeparator>
        <TimelineConnector />
        <TimelineDot>{resolvedIconName && <IconComponent iconName={resolvedIconName} />}</TimelineDot>
        <TimelineConnector />
      </TimelineSeparator>
      <TimelineContent sx={{ py: "12px", px: 2 }}>
        {contentChild && (
          <ComponentFactory
            parent={component}
            key={`${itemContext.StableId}-content-${contentChild.Id}`}
            component={contentChild}
            context={itemContext}
            componentPath={[...componentPath, contentChild.Id]}
          />
        )}
      </TimelineContent>
    </TimelineItem>
  );
};

// -----------------------------------------------------------------------
// Main Timeline component
// -----------------------------------------------------------------------
const TimelineComponent: React.FC<IComponentProps<TimelineModel>> = ({ component, context, componentPath = [] }) => {
  const [items, itemsPending] = useObservedExpression<any[]>(component.Items, context, "TimelineComponent", (value, error) => {
    if (error || value === undefined || value === null) return [];
    if (!Array.isArray(value)) {
      logger.error("Timeline Items expression did not return an array:", value);
      throw new Error("Timeline Items expression did not return an array: " + value);
    }
    return value;
  });

  const contextItems = useChildContexts(context, items, component.ItemKeyField, component.ItemVariableName ?? VariableNames.Item);

  const selectionProps = useComponentSelection({
    component,
    componentPath,
    isContainer: true,
    isEmpty: !items || items.length === 0,
  });

  const position = positionMap[component.Position ?? TimelinePosition.Alternate];

  return (
    <div style={{ position: "relative" }} {...selectionProps}>
      <LoadingOverlay loading={itemsPending} />
      <Timeline position={position}>
        {contextItems?.map(({ key, context: itemContext }, itemIndex) => (
          <TimelineItemRenderer
            key={`${itemContext.StableId}-${key}`}
            component={component}
            itemContext={itemContext}
            itemIndex={itemIndex}
            componentPath={componentPath}
            itemKey={key}
          />
        ))}
      </Timeline>
    </div>
  );
};

export default TimelineComponent;
